import { cloneDeep, isNil, orderBy } from 'lodash';

import { DBAction } from '../../../db/transaction/actions/db-action';
import { DBDeleteDoc } from '../../../db/transaction/actions/db-delete-doc';
import { DBUploadExistingDoc } from '../../../db/transaction/actions/db-upload-existing-doc';
import { DBTransaction } from '../../../db/transaction/db-transaction';
import { DBTransactionType } from '../../../db/transaction/db-transaction-type';
import { isNilOrDefault } from '../../generic/serialization/is-nil-or-default';
import { EventData } from '../event-data/event-data';
import { EventDataSchema } from '../event-data/event-data-schema';

/**
 * Merge the given events
 *
 * @param events
 * @returns
 */
export const mergeEvents = (events: EventData[]): DBTransaction<EventData>[] => {
  // Case: empty input
  if (events.length === 0) {
    return [];
  }

  // Sanity Check: only one type
  if (new Set(events.map((z) => z.getType())).size > 1) {
    throw new Error('mergeEvents: User Error: Events are not all of the same type.');
  }

  const mergeResults: DBTransaction<EventData>[] = [];
  const sortedEvents = orderBy(cloneDeep(events), [(z) => z.getStart().valueOf()], ['asc']);
  let mergeChunk = [sortedEvents[0]];
  for (let i = 1; i < events.length; i++) {
    if (isConsecutive(events[i], events[i - 1], false)) {
      mergeChunk.push(events[i]);
      continue;
    }

    mergeResults.push(mergeConsecutiveEvents(mergeChunk));
    mergeChunk = [events[i]];
  }

  // Merge any remaining events
  if (mergeChunk.length > 0) {
    mergeResults.push(mergeConsecutiveEvents(mergeChunk));
  }
  return mergeResults;
};

/**
 * Merge an array of consecutive events and autoresolve merge conflicts.
 * @param events
 */
const mergeConsecutiveEvents = (events: EventData[]): DBTransaction<EventData> => {
  if (events.length === 0) {
    throw new Error('mergeConsecutiveEvents: User Error, empty array');
  }

  // Variables
  const mergeResult = cloneDeep(events[0]).setEnd(events[events.length - 1].getEnd());
  const actions: DBAction<EventData>[] = [new DBUploadExistingDoc(mergeResult)];
  const schema = new EventDataSchema();
  const fields = [EventDataSchema.color, EventDataSchema.assignedUserId, EventDataSchema.assignedBackupUserId];
  for (let i = 1; i < events.length; i++) {
    // Merge
    const currEvent = events[i];

    // Sanity Check
    isConsecutive(events[i - 1], currEvent, true);

    // Delete this event
    actions.push(new DBDeleteDoc(currEvent));

    for (const fieldName of fields) {
      const oldValue = mergeResult.getField(fieldName);
      const newValue = currEvent.getField(fieldName);

      if (isNilOrDefault(newValue, fieldName, schema)) {
        continue;
      }

      if (isNilOrDefault(oldValue, fieldName, schema)) {
        mergeResult.setField(fieldName, newValue);
        continue;
      }
    }

    // Reset event configId if all events aren't from the same eventConfigId
    if (currEvent.getEventConfigId() !== mergeResult.getEventConfigId()) {
      mergeResult.setField(EventDataSchema.eventConfigId, EventDataSchema.Defaults.eventConfigId);
    }
  }

  // Update the action
  actions[0] = new DBUploadExistingDoc(mergeResult);
  return new DBTransaction(DBTransactionType.merge, actions);
};

/**
 * Assert that event2 starts when the event1 ends. If not, throw.
 * @param event1
 * @param event2
 * @param throwIfNot: Throw an error if the two events aren't consecutive
 */
const isConsecutive = (event1: EventData, event2: EventData, throwIfNotConsecutive: boolean): boolean => {
  if (isNil(event1) || isNil(event2)) {
    throw new Error('assertEventsConsecutive: User Error: Cannot compare if event is nil');
  }
  if (!event2.getStart().isSame(event1.getEnd())) {
    if (throwIfNotConsecutive) {
      throw new Error('assertEventsConsecutive: User Error: Events are not consecutive');
    }
    return false;
  }
  return true;
};
